/**
 * Treasury opening balance + projection logic (FIN V1 - Phase 7).
 *
 * Pure functions, integer cents arithmetic, no floating point.
 * Dates are stored/compared as ISO date strings (YYYY-MM-DD) in the
 * Europe/Madrid timezone for display; Date objects are intentionally
 * avoided to keep this module pure and timezone-stable.
 *
 * Additive model: snapshots are never deleted. For a given date, the
 * active snapshot is the most recently registered one. The opening
 * balance at any projection point is the active snapshot whose date is
 * the latest one on or before the forecast horizon.
 *
 * Formula (all integer cents):
 *   projectedBalanceCents =
 *     openingBalanceCents + cumulativeCollectionsCents - cumulativePaymentsCents
 */

export type TreasuryOpeningBalanceRole = "admin" | "admin_manager";

const OPENING_BALANCE_ROLES: ReadonlySet<string> = new Set<string>([
  "admin",
  "admin_manager",
]);

/** Returns true when `role` may register a treasury opening balance. */
export function canRegisterOpeningBalance(role: string | null | undefined): boolean {
  return Boolean(role) && OPENING_BALANCE_ROLES.has(role as string);
}

export interface TreasuryOpeningBalanceSnapshot {
  readonly id: string;
  /** ISO date string (YYYY-MM-DD) in Europe/Madrid. */
  readonly date: string;
  /** Integer cents (EUR). */
  readonly amountCents: number;
  readonly notes: string | null;
  /** Audit: id of the user that registered the snapshot. */
  readonly registeredBy: string;
  /** Audit: display name of the user that registered the snapshot. */
  readonly registeredByName: string;
  /** Audit: ISO timestamp of when the snapshot was registered. */
  readonly registeredAt: string;
}

export interface TreasuryBalanceBucketInput {
  readonly key: string;
  readonly label: string;
  /** ISO date string (YYYY-MM-DD). */
  readonly startsOn: string;
  /** ISO date string (YYYY-MM-DD). */
  readonly endsOn: string;
  readonly collectionsCents: number;
  readonly paymentsCents: number;
}

export interface TreasuryBalanceBucket extends TreasuryBalanceBucketInput {
  readonly openingBalanceCents: number;
  readonly netFlowCents: number;
  readonly finalBalanceCents: number;
}

export interface TreasuryBalanceProjection {
  readonly hasOpeningBalance: boolean;
  readonly openingBalanceCents: number;
  readonly openingDate: string | null;
  readonly cumulativeCollectionsCents: number;
  readonly cumulativePaymentsCents: number;
  readonly cumulativeNetFlowCents: number;
  readonly projectedBalanceCents: number;
  readonly buckets: readonly TreasuryBalanceBucket[];
}

/** Returns true when at least one snapshot has been registered. */
export function hasOpeningBalanceSnapshot(
  snapshots: readonly TreasuryOpeningBalanceSnapshot[],
): boolean {
  return snapshots.length > 0;
}

/**
 * Returns the active snapshot for the given ISO date, or null when none exists.
 *
 * Additive model: snapshots are never removed. When several snapshots share
 * the same date, the one with the latest `registeredAt` wins. Snapshots whose
 * date is strictly after `onOrBefore` are ignored so the projection is
 * deterministic against the forecast horizon.
 */
export function findActiveOpeningBalance(
  snapshots: readonly TreasuryOpeningBalanceSnapshot[],
  onOrBefore?: string,
): TreasuryOpeningBalanceSnapshot | null {
  if (snapshots.length === 0) return null;
  const eligible = onOrBefore
    ? snapshots.filter((snapshot) => snapshot.date <= onOrBefore)
    : snapshots.slice();
  if (eligible.length === 0) return null;
  const sorted = eligible.slice().sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? 1 : -1;
    if (a.registeredAt !== b.registeredAt) {
      return a.registeredAt < b.registeredAt ? 1 : -1;
    }
    return a.id < b.id ? 1 : -1;
  });
  return sorted[0] ?? null;
}

/**
 * Core projection formula (all integer cents):
 *   projectedBalanceCents =
 *     openingBalanceCents + cumulativeCollectionsCents - cumulativePaymentsCents
 *
 * Each bucket chains from the previous bucket's final balance so the running
 * balance stays consistent across the horizon.
 */
export function computeProjectedBalance(
  snapshots: readonly TreasuryOpeningBalanceSnapshot[],
  buckets: readonly TreasuryBalanceBucketInput[],
): TreasuryBalanceProjection {
  const active = findActiveOpeningBalance(snapshots);
  if (!active) {
    return {
      hasOpeningBalance: false,
      openingBalanceCents: 0,
      openingDate: null,
      cumulativeCollectionsCents: 0,
      cumulativePaymentsCents: 0,
      cumulativeNetFlowCents: 0,
      projectedBalanceCents: 0,
      buckets: [],
    };
  }
  let running = active.amountCents;
  let cumulativeCollections = 0;
  let cumulativePayments = 0;
  const enriched: TreasuryBalanceBucket[] = [];
  for (const bucket of buckets) {
    const opening = running;
    const collections = Math.trunc(bucket.collectionsCents) || 0;
    const payments = Math.trunc(bucket.paymentsCents) || 0;
    const netFlow = collections - payments;
    const finalBalance = opening + netFlow;
    enriched.push({
      key: bucket.key,
      label: bucket.label,
      startsOn: bucket.startsOn,
      endsOn: bucket.endsOn,
      collectionsCents: collections,
      paymentsCents: payments,
      openingBalanceCents: opening,
      netFlowCents: netFlow,
      finalBalanceCents: finalBalance,
    });
    running = finalBalance;
    cumulativeCollections += collections;
    cumulativePayments += payments;
  }
  const cumulativeNetFlow = cumulativeCollections - cumulativePayments;
  const projected = active.amountCents + cumulativeCollections - cumulativePayments;
  return {
    hasOpeningBalance: true,
    openingBalanceCents: active.amountCents,
    openingDate: active.date,
    cumulativeCollectionsCents: cumulativeCollections,
    cumulativePaymentsCents: cumulativePayments,
    cumulativeNetFlowCents: cumulativeNetFlow,
    projectedBalanceCents: projected,
    buckets: enriched,
  };
}

/**
 * Converts an EUR display string (e.g. "1.234,56" or "1234.56") to integer
 * cents. Returns 0 for empty or invalid input. Pure integer math, no
 * floating-point drift.
 */
export function euroToCents(input: string): number {
  if (!input) return 0;
  const trimmed = input.trim().replace(/\s+/g, "").replace(/[€$]/g, "");
  if (!trimmed) return 0;
  const sign = trimmed.startsWith("-") ? -1 : 1;
  const raw = trimmed.replace(/^-/, "");
  const lastPeriod = raw.lastIndexOf(".");
  const lastComma = raw.lastIndexOf(",");
  let digits: string;
  if (lastComma > lastPeriod) {
    digits = raw.replace(/\./g, "").replace(",", ".");
  } else if (lastPeriod > lastComma) {
    const afterPeriod = raw.slice(lastPeriod + 1);
    if (afterPeriod.length <= 2 && /^\d+$/.test(afterPeriod)) {
      digits = raw.replace(/,/g, "");
    } else {
      digits = raw.replace(/\./g, "");
    }
  } else {
    digits = raw;
  }
  const match = digits.match(/^(\d+)(?:\.(\d{1,2}))?$/);
  if (!match) return 0;
  const whole = parseInt(match[1], 10) || 0;
  const fracRaw = match[2] ?? "";
  const frac = (fracRaw + "00").slice(0, 2);
  const cents = whole * 100 + parseInt(frac, 10);
  return sign * cents;
}

/**
 * Returns the ISO date string (YYYY-MM-DD) for a Date in the Europe/Madrid
 * timezone. Used for the display layer; the snapshot stores the canonical
 * ISO string provided by the caller.
 */
export function toMadridDateString(value: Date): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(value);
}

/**
 * Returns today's ISO date string (YYYY-MM-DD) in Europe/Madrid. Kept as a
 * named export so callers and tests do not need to construct Date objects.
 */
export function todayMadridDateString(now: Date = new Date()): string {
  return toMadridDateString(now);
}
